import * as React from 'react';
import { useIntl } from 'react-intl';
import { NumericFormat } from 'react-number-format';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Box, Button, Card, IconButton } from '@mui/material';
import MuiTextField from '@mui/material/TextField';
import MuiSelect from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { IntlMessage, useLocale } from 'app/components/Intl';
import { isNil, parseDate, serializeDate } from 'app/utils';

import NumericInputHelper from 'app/components/FormikField/Text/NumericInputHelper';

import type { RegulationsField, RegulationsFieldTableColumn } from './RegulationsField.types';
import { useRegulationsFieldValue } from './useRegulationsFieldValue';
import {
  AddIcon,
  DeleteIcon,
  ExpandLessIcon,
  ExpandMoreIcon,
  PaperStyled,
  TableBodyStyled,
  TableCellStyled,
  TableHeadStyled,
  TableRowStyled,
  TableStyled,
} from './RegulationsTableField.styles';

export type RegulationsTableFieldProps = {
  field: RegulationsField;
  className?: string;
  isEditing?: boolean;
};

type TableRowData = Record<string, any>;

const prepareRowsForCommit = (
  rows: Array<TableRowData>,
  columns: Array<RegulationsFieldTableColumn>,
): Array<TableRowData> => {
  const filledRows = rows.filter(row => columns.every(column => !isNil(row[column.field]) && row[column.field] !== ''));

  const [firstColumn] = columns;
  if (!firstColumn) {
    return filledRows;
  }

  return [...filledRows].sort((a, b) => {
    const valueA = String(a[firstColumn.field]).toLowerCase();
    const valueB = String(b[firstColumn.field]).toLowerCase();
    return valueA.localeCompare(valueB);
  });
};

export const parseRegulationsTableRows = (value: any): Array<TableRowData> => {
  let rows: Array<any> = [];

  if (Array.isArray(value)) {
    rows = value;
  } else if (typeof value === 'string' && value) {
    try {
      const parsed = JSON.parse(value);
      rows = Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return rows.reduce<Array<TableRowData>>((result, row) => {
    if (typeof row !== 'object' || row === null || Array.isArray(row)) {
      return result;
    }

    try {
      const jsonRow = JSON.parse(JSON.stringify(row));
      if (typeof jsonRow === 'object' && jsonRow !== null && !Array.isArray(jsonRow)) {
        result.push(jsonRow);
      }
    } catch {
      // Ignore values that cannot be represented as JSON objects.
    }

    return result;
  }, []);
};

type TableCellFieldProps = {
  column: RegulationsFieldTableColumn;
  value: any;
  disabled?: boolean;
  onChange: (value: any) => void;
};

const TableCellField = ({ column, value, disabled, onChange }: TableCellFieldProps) => {
  const intl = useIntl();
  const { locale } = useLocale();
  const type = column.fieldType || 'text';
  const options = column.options || {};
  const label = intl.formatMessage({ id: column.header });

  if (type === 'select' || type === 'list') {
    const items = (column.options as any)?.options || [];
    const labelId = `${column.field}-label`;
    return (
      <FormControl fullWidth>
        <InputLabel id={labelId} shrink>
          {label}
        </InputLabel>
        <MuiSelect
          labelId={labelId}
          label={label}
          notched
          value={value ?? ''}
          disabled={disabled}
          onChange={event => onChange(event.target.value)}
        >
          {items.map((item: any) => {
            const itemValue = item.value ?? item;
            return (
              <MenuItem key={`${itemValue}`} value={itemValue}>
                {item.element ?? itemValue}
              </MenuItem>
            );
          })}
        </MuiSelect>
      </FormControl>
    );
  }

  if (type === 'date') {
    const { localDate = true, startOfDay, endOfDay } = options;
    const dateValue = isNil(value) || value === '' ? null : parseDate(value, localDate);

    return (
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
        <MuiDatePicker
          value={dateValue}
          disabled={disabled}
          onChange={newValue => onChange(serializeDate(newValue, localDate, startOfDay, endOfDay))}
          slotProps={{
            textField: {
              fullWidth: true,
              label,
              InputLabelProps: { shrink: true },
              InputProps: { notched: true },
            },
          }}
        />
      </LocalizationProvider>
    );
  }

  if (type === 'number' || type === 'float' || type === 'currency') {
    const numericHelper = new NumericInputHelper(intl.locale, options);
    return (
      <NumericFormat
        customInput={MuiTextField}
        label={label}
        InputLabelProps={{ shrink: true }}
        InputProps={{ notched: true }}
        fullWidth
        disabled={disabled}
        value={numericHelper.getValue(value)}
        onValueChange={values => onChange(numericHelper.getValue(values.value))}
        {...numericHelper.getProps()}
      />
    );
  }

  return (
    <MuiTextField
      label={label}
      InputLabelProps={{ shrink: true }}
      InputProps={{ notched: true }}
      fullWidth
      disabled={disabled}
      value={value ?? ''}
      onChange={event => onChange(event.target.value)}
    />
  );
};

const RegulationsTableFieldComponent = ({ field, className, isEditing = true }: RegulationsTableFieldProps) => {
  const { name, value, defaultValue, disabled = false } = field;
  const columns = React.useMemo(() => field.options?.columns ?? [], [field.options?.columns]);

  const initialValue = React.useMemo(
    () => parseRegulationsTableRows(isNil(value) ? defaultValue : value),
    [value, defaultValue],
  );

  const [, commit] = useRegulationsFieldValue<Array<TableRowData>>(name, initialValue);
  const [rows, setRows] = React.useState<Array<TableRowData>>(initialValue);

  const commitRows = React.useCallback((nextRows: unknown) => commit(parseRegulationsTableRows(nextRows)), [commit]);

  React.useEffect(() => {
    setRows(initialValue);
  }, [initialValue]);

  React.useEffect(() => {
    commitRows(initialValue);
  }, [commitRows, initialValue]);

  const [collapsed, setCollapsed] = React.useState(false);

  const toggleCollapsed = React.useCallback(() => setCollapsed(current => !current), []);

  const updateCell = React.useCallback(
    (rowIndex: number, columnField: string, cellValue: any) => {
      setRows(currentRows => {
        const nextRows = currentRows.map((row, index) =>
          index === rowIndex ? { ...row, [columnField]: cellValue } : row,
        );
        commitRows(prepareRowsForCommit(nextRows, columns));
        return nextRows;
      });
    },
    [columns, commitRows],
  );

  const addRow = React.useCallback(() => {
    const emptyRow = columns.reduce<TableRowData>((acc, column) => {
      acc[column.field] = '';
      return acc;
    }, {});
    setRows(currentRows => {
      const nextRows = [...currentRows, emptyRow];
      commitRows(prepareRowsForCommit(nextRows, columns));
      return nextRows;
    });
  }, [columns, commitRows]);

  const deleteRow = React.useCallback(
    (rowIndex: number) => {
      setRows(currentRows => {
        const nextRows = currentRows.filter((_, index) => index !== rowIndex);
        commitRows(prepareRowsForCommit(nextRows, columns));
        return nextRows;
      });
    },
    [columns, commitRows],
  );

  return (
    <PaperStyled className={className}>
      <Card>
        <PerfectScrollbar>
          <TableStyled size="small">
            <TableHeadStyled>
              <TableRowStyled>
                {columns.map(column => (
                  <TableCellStyled key={column.field}>
                    <IntlMessage value={column.header} />
                  </TableCellStyled>
                ))}
                <TableCellStyled sx={{ width: '60px', minWidth: '60px', textAlign: 'center' }}>
                  <IconButton size="small" onClick={toggleCollapsed}>
                    {collapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                  </IconButton>
                </TableCellStyled>
              </TableRowStyled>
            </TableHeadStyled>
            {!collapsed && (
              <TableBodyStyled>
                {rows.map((row, rowIndex) => (
                  <TableRowStyled key={rowIndex} hover>
                    {columns.map((column, colIndex) => (
                      <TableCellStyled
                        key={column.field}
                        colSpan={!isEditing && colIndex === columns.length - 1 ? 2 : 1}
                      >
                        {isEditing ? (
                          <TableCellField
                            column={column}
                            value={row[column.field]}
                            disabled={disabled}
                            onChange={cellValue => updateCell(rowIndex, column.field, cellValue)}
                          />
                        ) : (
                          row[column.field] ?? ''
                        )}
                      </TableCellStyled>
                    ))}
                    {isEditing && (
                      <TableCellStyled sx={{ width: '60px', minWidth: '60px', textAlign: 'center' }}>
                        <IconButton size="small" disabled={disabled} onClick={() => deleteRow(rowIndex)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCellStyled>
                    )}
                  </TableRowStyled>
                ))}
              </TableBodyStyled>
            )}
          </TableStyled>
        </PerfectScrollbar>
        {isEditing && !collapsed && (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 1 }}>
            <Button variant="outlined" size="small" startIcon={<AddIcon />} disabled={disabled} onClick={addRow}>
              <IntlMessage value={'button.addRow'} />
            </Button>
          </Box>
        )}
      </Card>
    </PaperStyled>
  );
};

export const RegulationsTableField = React.memo(RegulationsTableFieldComponent);
